import { NavBar } from 'components/NavBar/NavBar'
import { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import {
    Animation,
    Box,
    Button,
    CursorPointer,
    Flex,
    H1WithBack,
    ItemsGrid,
    PageWrapper,
    Tabs,
    TabsContainer,
    ThinSeparator,
} from 'styling/GlobalStyles'
import {
    convertMinsToHrsMins,
    formatDateForDisplay,
    formatNumbersForDisplay,
} from 'utils/helpers'
import {
    EmployeeActivityList,
    EmployeeActivityRow,
    EmployeeContractDownloadRow,
    EmployeeContractSummaryCard,
    EmployeeContractsColumn,
    EmployeeHeaderAvatar,
    EmployeeHeaderCard,
    EmployeeHeaderIdentity,
    EmployeeHeaderMeta,
    EmployeeHeaderRight,
    EmployeeHeaderTopRow,
    EmployeeInfoField,
    EmployeeInfoGrid,
    EmployeeOverviewGrid,
    EmployeeOverviewMain,
    EmployeeOverviewSide,
    EmployeePointsPill,
    EmployeeRatingInline,
    EmployeeSchedulecContainer,
    EmployeeSectionHeading,
    EmployeeSideCard,
    EmployeeStatCard,
    EmployeeStatCardsRow,
    EmployeeTabPanel,
    EmployeeTaskList,
    EmployeeTaskRow,
    EmployeeTaskStatusBadge,
    EmployeeTodayStatusRow,
    IconContainer,
} from '../Dashboard.styled'
import {
    ListSidebarGrid,
    ListTable,
    ListTableHeader,
    ListTableRow,
    PerformanceCard,
    PerformanceCardHeader,
    PerformanceCertificateAlert,
    PerformanceDailyBar,
    PerformanceDailyBars,
    PerformanceDonutContent,
    PerformanceFootnote,
    PerformanceGrid,
    PerformanceLegendList,
    PerformanceLegendRow,
    PerformanceMachineRow,
    PerformanceStatCol,
    PerformanceStatsRow,
    PreviewModalField,
    PreviewModalGrid,
    PreviewModalLink,
    SidePanelCard,
} from './DBSingleEmployee.styled'
import { Input } from 'components/Input/Input'
import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import { HonestSelectAsyncInput } from 'components/HonestAsyncSelect/HonestAsyncSelectInput'
import CalendarInput from 'components/CalendarInput/CalendarInput'
import { DropZone } from 'components/DropZone/DropZone'
import { ScheduleWeeklyCard } from 'pages/Shifts/Shifts.styled'
import Avatar from '../../../assets/images/profileAvatar.png'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import { useParams } from 'react-router-dom'
import {
    addDBEmployeeDocument,
    addDBEmployeeNote,
    clearDBEmployeeContract,
    clearDBEmployeeContracts,
    clearDBEmployeeDocuments,
    clearDBEmployeeHeader,
    clearDBEmployeeNotes,
    clearDBEmployeeOverview,
    clearDBEmployeePerformance,
    getDBEmployeeContract,
    getDBEmployeeContracts,
    getDBEmployeeDocuments,
    getDBEmployeeHeader,
    getDBEmployeeNotes,
    getDBEmployeeOverview,
    getDBEmployeeOverviewLastTasks,
    getDBEmployeeOverviewRecentActivity,
    getDBEmployeePerformance,
} from 'features/dashboard/dbEmployeesSlice'
import { taskStatusesData } from '../dashboardData'
import useQueryPagination from 'hooks/useQueryPagination'
import { BodyText, OtherText, ParagraphText, TitleText } from 'styling/Typography'
import { CloseIcon } from 'assets/icons/CloseIcon'
import { BigStar } from 'assets/icons/BigStar'
import { TrophyIcon } from 'assets/icons/TrophyIcon'
import { DownloadIcon } from 'assets/icons/DownloadIcon'
import { dietaryPreferences } from 'pages/Food/foodData'
import {
    contractTypes,
    educationLevelOptions,
    emergencyContactOptions,
    logisticsTransportationTypes,
} from 'pages/Employees/employeesData'
import {
    documentAccessRestrictionsOptions,
    documentCategoryOptions,
    genderList,
} from 'pages/Employees/constants'
import { tierOptions } from 'pages/Accomodation/Sections/CheckIn'
import { BeachIcon } from './icons/BeachIcon'
import { CheckIcon } from './icons/CheckIcon'
import { ClockIcon } from './icons/ClockIcon'
import { PinIcon } from './icons/PinIcon'
import { SuitCaseIcon } from './icons/SuitCaseIcon'
import { TruckIcon } from './icons/TruckIcon'
import { WrenchIcon } from './icons/WrenchIcon'
import { AlertIcon as PerfAlertIcon } from './icons/AlertIcon'
import { SunriseIcon } from 'pages/Shifts/Icons/SunriseIcon'
import { SunsetIcon } from 'pages/Shifts/Icons/SunsetIcon'
import { RotationIcon } from 'pages/Shifts/Icons/RotationIcon'
import { BeachIcon as ShiftBeachIcon } from 'pages/Shifts/Icons/BeachIcon'
import { DrinkIcon } from 'pages/Shifts/Icons/DrinkIcon'
import { HeartIcon } from 'pages/Shifts/Icons/HeartIcon'
import { SunIcon } from 'pages/Shifts/Icons/SunIcon'
import { MinusIcon } from 'pages/Shifts/Icons/MinusIcon'
import { Modal } from 'components/Modal/Modal'
import useModalClose from 'hooks/useModalClose'
import { IDBEmployeeDocument, IDBEmployeeNote, IDBEmployeeShift } from 'features/dashboard/types'
import { toast } from 'react-toastify'

const TABS = [
    'Overview',
    'Performance',
    'Basic Info',
    'Welfare',
    'Contract',
    'Bank Info',
    'Skill Wallet',
    'Documents',
    'Notes',
] as const
type TabKey = (typeof TABS)[number]

const PAGE_LIMIT = 10
const DONUT_COLORS = ['#FF9900', '#00A6FF', '#AF52DE', '#24D07B', '#FF4F46']

const noteVisibilityScopeOptions = [
    { value: 1, label: 'Restricted' },
    { value: 2, label: 'General' },
]

const noteCategoryOptions = [
    { value: 1, label: 'HR' },
    { value: 2, label: 'Ops' },
    { value: 3, label: 'Compliance' },
    { value: 4, label: 'Welfare' },
]

const getShiftIcon = (iconName: string, color: string) => {
    switch (iconName) {
        case 'sunrise': return <SunriseIcon stroke={color || '#ff9900'} />
        case 'sunset': return <SunsetIcon stroke={color || '#ff9900'} />
        case 'rotation': return <RotationIcon stroke={color || '#ff9900'} />
        case 'beach': return <ShiftBeachIcon stroke={color || '#ff9900'} />
        case 'drink': return <DrinkIcon stroke={color || '#ff9900'} />
        case 'heart': return <HeartIcon stroke={color || '#ff9900'} />
        case 'sun': return <SunIcon stroke={color || '#ff9900'} />
        default: return <SunriseIcon stroke={color || '#ff9900'} />
    }
}

const formatDateShort = (date: Date): string => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    const dayAbbr = days[date.getDay()]
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    return `${dayAbbr} ${day}.${month}.`
}

const Schedule = ({ entry }: { entry: IDBEmployeeShift }) => {
    const date = entry.date ? new Date(entry.date) : null
    const shift = entry.shift
    const hasShift = !!shift?.name && shift.name.trim().length > 0
    const cardColor = hasShift && shift?.color ? '#' + shift.color : null
    const textColor = hasShift && shift?.textColor ? '#' + shift.textColor : '#fff'
    const showTime = hasShift && !shift.absence && Number.isFinite(shift.shiftStart)
    return (
        <Flex gap='10px' column width='120px' minWidth='120px'>
            <OtherText>{date ? formatDateShort(date) : '-'}</OtherText>
            <ScheduleWeeklyCard color={cardColor}>
                <Flex center gap='0.5rem' style={{ overflow: 'hidden', width: '100%' }}>
                    <span style={{ flexShrink: 0 }}>
                        {hasShift
                            ? getShiftIcon(shift.icon, shift.iconColor ? '#' + shift.iconColor : '#fff')
                            : <MinusIcon stroke='#fff' />}
                    </span>
                    <Flex column style={{ minWidth: 0, overflow: 'hidden' }}>
                        <BodyText
                            style={{
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                                overflow: 'hidden',
                                maxWidth: '100%',
                                color: textColor,
                            }}
                            weight='bold'
                        >
                            {hasShift ? shift.name : 'Not Set'}
                        </BodyText>
                        {showTime && (
                            <ParagraphText style={{ color: textColor, whiteSpace: 'nowrap' }}>
                                {convertMinsToHrsMins(shift.shiftStart)}-{convertMinsToHrsMins(shift.minutes, shift.shiftStart)}
                            </ParagraphText>
                        )}
                    </Flex>
                </Flex>
            </ScheduleWeeklyCard>
        </Flex>
    )
}

const Field = ({ label, value }: { label: string; value?: string | number | null }) => (
    <EmployeeInfoField>
        <span className='field-label'>{label}</span>
        <span className='field-value'>{value || value === 0 ? value : '-'}</span>
    </EmployeeInfoField>
)

export const DBSingleEmployee = () => {
    const { id } = useParams()
    const dispatch = useDispatch<AppDispatch>()
    const {
        header,
        overview,
        overviewLastTasks,
        overviewRecentActivity,
        performance,
        contract,
        contracts,
        documents,
        notes,
    } = useSelector((state: RootState) => state.dbEmployees)
    const [active, setActive] = useState<TabKey>('Overview')
    const [docPage, setDocPage] = useState(1)
    const [notePage, setNotePage] = useState(1)
    const [contractsPage, setContractsPage] = useState(1)
    const [previewDoc, setPreviewDoc] = useState<IDBEmployeeDocument | null>(null)
    const [previewNote, setPreviewNote] = useState<IDBEmployeeNote | null>(null)
    const [contractId, setContractId] = useState<number | null>(null)
    const [contractModalOpen, setContractModalOpen] = useState(false)
    const [noteDraft, setNoteDraft] = useState<{ content: string; visibilityScope: number | null; category: number | null }>({
        content: '',
        visibilityScope: null,
        category: null,
    })
    const [docDraft, setDocDraft] = useState<{
        documentFile: File | null
        capability: any
        category: number | null
        accessRestrictions: number | null
        effectiveDate: Date | null
        expiryDate: Date | null
    }>({
        documentFile: null,
        capability: null,
        category: null,
        accessRestrictions: null,
        effectiveDate: null,
        expiryDate: null,
    })

    const headerParams = useMemo(() => ({ userId: id }), [id])
    useQueryPagination({
        fetchFunctions: [getDBEmployeeHeader],
        cleanUpFunctions: [
            clearDBEmployeeHeader,
            clearDBEmployeeOverview,
            clearDBEmployeePerformance,
            clearDBEmployeeContract,
            clearDBEmployeeContracts,
            clearDBEmployeeDocuments,
            clearDBEmployeeNotes,
        ],
        params: headerParams,
    })

    useEffect(() => {
        if (!id) return
        if (active === 'Overview') {
            dispatch(getDBEmployeeOverview({ userId: id }))
            dispatch(getDBEmployeeOverviewLastTasks({ userId: id }))
            dispatch(getDBEmployeeOverviewRecentActivity({ userId: id, page: 1, limit: 10 }))
        } else if (active === 'Performance') {
            dispatch(getDBEmployeePerformance({ userId: id }))
        } else if (active === 'Documents') {
            dispatch(getDBEmployeeDocuments({ userId: id, page: docPage, limit: PAGE_LIMIT }))
        } else if (active === 'Notes') {
            dispatch(getDBEmployeeNotes({ userId: id, page: notePage, limit: PAGE_LIMIT }))
        } else if (active === 'Contract') {
            dispatch(getDBEmployeeContracts({ userId: id, page: contractsPage, limit: PAGE_LIMIT }))
            if (contractId) {
                dispatch(getDBEmployeeContract({ userId: id, contractId }))
            }
        }
    }, [active, id, docPage, notePage, contractsPage, contractId, dispatch])

    useModalClose(() => {
        setPreviewDoc(null)
        setPreviewNote(null)
        setContractModalOpen(false)
    })

    const handleAddNote = async () => {
        if (!id) return
        if (!noteDraft.content?.trim()) {
            toast.warn('Please fill in the note content before saving.')
            return
        }
        if (!noteDraft.visibilityScope) {
            toast.warn('Please select a visibility scope.')
            return
        }
        if (!noteDraft.category) {
            toast.warn('Please select a category.')
            return
        }
        const resp = await dispatch(
            addDBEmployeeNote({
                userId: id,
                content: noteDraft.content.trim(),
                visibilityScope: noteDraft.visibilityScope,
                category: noteDraft.category,
            }),
        )
        if (resp.meta.requestStatus === 'fulfilled') {
            toast.success('Note added.')
            setNoteDraft({ content: '', visibilityScope: null, category: null })
            dispatch(getDBEmployeeNotes({ userId: id, page: notePage, limit: PAGE_LIMIT }))
        }
    }

    const handleAddDocument = async () => {
        if (!id) return
        if (!docDraft.documentFile) {
            toast.warn('Please upload a document file before saving.')
            return
        }
        if (!docDraft.capability?.value) {
            toast.warn('Please select a capability before saving.')
            return
        }
        if (!docDraft.category) {
            toast.warn('Please select a category.')
            return
        }
        if (!docDraft.accessRestrictions) {
            toast.warn('Please select access restrictions.')
            return
        }
        if (docDraft.capability?.requiresExpiry && (!docDraft.effectiveDate || !docDraft.expiryDate)) {
            toast.warn('Please fill in effective and expiration dates for this capability.')
            return
        }
        const resp = await dispatch(
            addDBEmployeeDocument({
                userId: id,
                documentFile: docDraft.documentFile,
                capabilityId: docDraft.capability.value,
                category: docDraft.category,
                accessRestrictions: docDraft.accessRestrictions,
                effectiveDate: docDraft.effectiveDate,
                expiryDate: docDraft.expiryDate,
            }),
        )
        if (resp.meta.requestStatus === 'fulfilled') {
            toast.success('Document added.')
            setDocDraft({
                documentFile: null,
                capability: null,
                category: null,
                accessRestrictions: null,
                effectiveDate: null,
                expiryDate: null,
            })
            dispatch(getDBEmployeeDocuments({ userId: id, page: docPage, limit: PAGE_LIMIT }))
        }
    }

    const renderSchedule = (): IDBEmployeeShift[] => {
        const shifts = header?.shifts || []

        const dayKey = (d: Date) => {
            const y = d.getFullYear()
            const m = String(d.getMonth() + 1).padStart(2, '0')
            const dd = String(d.getDate()).padStart(2, '0')
            return `${y}-${m}-${dd}`
        }

        const refDate = shifts.length
            ? new Date(Math.min(...shifts.map((s) => (s.date ? new Date(s.date).getTime() : Date.now()))))
            : new Date()

        const weekStart = new Date(refDate)
        const dow = weekStart.getDay()
        const diff = dow === 0 ? -6 : 1 - dow
        weekStart.setDate(weekStart.getDate() + diff)
        weekStart.setHours(0, 0, 0, 0)

        const shiftByDay = new Map<string, IDBEmployeeShift>()
        shifts.forEach((s) => {
            if (s.date) shiftByDay.set(dayKey(new Date(s.date)), s)
        })

        const result: IDBEmployeeShift[] = []
        for (let i = 0; i < 7; i++) {
            const date = new Date(weekStart)
            date.setDate(weekStart.getDate() + i)
            const existing = shiftByDay.get(dayKey(date))
            if (existing) {
                result.push(existing)
            } else {
                result.push({ date: `${dayKey(date)}T00:00:00`, shift: null as unknown as IDBEmployeeShift['shift'] })
            }
        }
        return result
    }

    const basicInfo = header?.basicInfo
    const welfare = header?.welfare
    const bankInfo = header?.bankInfo
    const contractData = contract?.contract

    const dietary = dietaryPreferences.find((p) => p.value == welfare?.dietaryPreferences)?.label
    const education = educationLevelOptions.find((l) => l.value == basicInfo?.educationLevel)?.label
    const transportType = logisticsTransportationTypes.find((t) => t.value == welfare?.transportationType)?.label
    const accommodationTier = tierOptions.find((t) => t.value == welfare?.accommodationTierPolicy)?.label
    const relationLabel = emergencyContactOptions.find((o) => o.value == basicInfo?.emergencyContact?.relationType)?.label
    const genderLabel = genderList.find((g) => g.value == basicInfo?.gender)?.label || basicInfo?.gender

    const stats = overview?.stats
    const overviewContract = overview?.contract
    const todayStatus = overview?.todayStatus

    const getTaskBadgeStatus = (statusId: number): 'completed' | 'progress' | 'open' => {
        const status = taskStatusesData.find((s) => s.id == statusId)
        const name = status?.name?.toLowerCase() || ''
        if (name.includes('complete')) return 'completed'
        if (name.includes('progress')) return 'progress'
        return 'open'
    }

    const renderOverview = () => (
        <EmployeeOverviewGrid>
            <EmployeeOverviewMain>
                <EmployeeStatCardsRow>
                    <EmployeeStatCard>
                        <IconContainer color='#AF52DE1A'>
                            <BeachIcon />
                        </IconContainer>
                        <div className='stat-text'>
                            <span className='stat-label'>Vacation Used</span>
                            <span className='stat-value'>
                                {stats?.vacationUsed ?? 0}
                                <span className='stat-value-sub'>/{stats?.vacationTotal ?? 0}</span>
                            </span>
                        </div>
                    </EmployeeStatCard>
                    <EmployeeStatCard>
                        <IconContainer color='#00A6FF26'>
                            <BeachIcon color='#00A6FF' />
                        </IconContainer>
                        <div className='stat-text'>
                            <span className='stat-label'>Days Off</span>
                            <span className='stat-value'>
                                {stats?.daysOff ?? 0}
                                <span className='stat-value-sub'>/{stats?.daysOffTotal ?? 0}</span>
                            </span>
                        </div>
                    </EmployeeStatCard>
                    <EmployeeStatCard>
                        <IconContainer color='#24D07B1A'>
                            <CheckIcon />
                        </IconContainer>
                        <div className='stat-text'>
                            <span className='stat-label'>Tasks This Month</span>
                            <span className='stat-value'>{stats?.tasksThisMonth ?? 0}</span>
                        </div>
                    </EmployeeStatCard>
                    <EmployeeStatCard>
                        <IconContainer color='#FF99001A'>
                            <ClockIcon />
                        </IconContainer>
                        <div className='stat-text'>
                            <span className='stat-label'>Open Tasks</span>
                            <span className='stat-value'>
                                {stats?.tasksTodayCompleted ?? 0}
                                <span className='stat-value-sub'>/{stats?.tasksTodayTotal ?? 0}</span>
                            </span>
                        </div>
                    </EmployeeStatCard>
                </EmployeeStatCardsRow>
                <EmployeeContractSummaryCard>
                    <IconContainer color='#FF99001A'>
                        <SuitCaseIcon />
                    </IconContainer>
                    <div className='rows'>
                        <Field label='Contract' value={overviewContract?.contractTypeLabel} />
                        <Field label='End Date' value={overviewContract?.endDate ? formatDateForDisplay(overviewContract.endDate) : '-'} />
                        <Field label='Days Remaining' value={overviewContract?.daysRemaining} />
                        <Field
                            label='Accommodation'
                            value={[overviewContract?.accommodationBlockName, overviewContract?.accommodationRoomName].filter(Boolean).join(' / ')}
                        />
                    </div>
                </EmployeeContractSummaryCard>

                <EmployeeSideCard>
                    <div className='side-header'>
                        <span className='title'>RECENT ACTIVITY</span>
                    </div>
                    <EmployeeActivityList>
                        {overviewRecentActivity?.data?.length ? (
                            overviewRecentActivity.data.slice(0, 6).map((a, i) => (
                                <EmployeeActivityRow key={i}>
                                    <ClockIcon />
                                    <span className='activity-text'>{a.description}</span>
                                    <span className='activity-time'>
                                        {a.occurredAt ? formatDateForDisplay(a.occurredAt) : '-'}
                                    </span>
                                </EmployeeActivityRow>
                            ))
                        ) : (
                            <OtherText grey>No recent activity</OtherText>
                        )}
                    </EmployeeActivityList>
                </EmployeeSideCard>
            </EmployeeOverviewMain>

            <EmployeeOverviewSide>
                <EmployeeSideCard>
                    <div className='side-header'>
                        <span className='title'>LAST TASKS</span>
                        <span className='view-all'>View all ›</span>
                    </div>
                    <EmployeeTaskList>
                        <EmployeeTaskRow>
                            <span className='task-header'>Name</span>
                            <span className='task-header'>Status</span>
                        </EmployeeTaskRow>
                        {overviewLastTasks?.length ? (
                            overviewLastTasks.slice(0, 5).map((t) => (
                                <EmployeeTaskRow key={t.taskId}>
                                    <span className='task-name'>{t.taskName}</span>
                                    <EmployeeTaskStatusBadge status={getTaskBadgeStatus(t.taskStatus)}>
                                        {taskStatusesData.find((s) => s.id == t.taskStatus)?.name || '-'}
                                    </EmployeeTaskStatusBadge>
                                </EmployeeTaskRow>
                            ))
                        ) : (
                            <OtherText grey>No tasks</OtherText>
                        )}
                    </EmployeeTaskList>
                </EmployeeSideCard>

                <EmployeeSideCard>
                    <div className='side-header'>
                        <span className='title'>TODAY&apos;S STATUS</span>
                    </div>
                    <EmployeeTodayStatusRow>
                        <IconContainer color='#FF99001A'>
                            <ClockIcon />
                        </IconContainer>
                        <div className='info'>
                            <span className='info-label'>Current Shift</span>
                            <span className='info-value'>{todayStatus?.currentShiftName || '-'}</span>
                        </div>
                    </EmployeeTodayStatusRow>
                    <EmployeeTodayStatusRow>
                        <IconContainer color='#00A6FF1A'>
                            <PinIcon />
                        </IconContainer>
                        <div className='info'>
                            <span className='info-label'>Location</span>
                            <span className='info-value'>{todayStatus?.locationName || '-'}</span>
                        </div>
                    </EmployeeTodayStatusRow>
                    <EmployeeTodayStatusRow>
                        <IconContainer color='#AF52DE1A'>
                            <TruckIcon />
                        </IconContainer>
                        <div className='info'>
                            <span className='info-label'>Assigned Machine</span>
                            <span className='info-value'>{todayStatus?.assignedMachineName || '-'}</span>
                        </div>
                    </EmployeeTodayStatusRow>
                </EmployeeSideCard>
            </EmployeeOverviewSide>
        </EmployeeOverviewGrid>
    )

    const renderPerformance = () => {
        const att = performance?.attendance
        const tasks = performance?.tasks
        const certs = performance?.certificates
        const totalTasksRate = tasks?.tasksRates?.reduce((sum, t) => sum + (t.taskRate || 0), 0) || 0

        const renderDailyBars = () => {
            const logs = att?.dailyLogs || []
            const slots = logs.length ? logs : Array.from({ length: 22 }, () => ({ date: '', status: 0 }))
            return (
                <PerformanceDailyBars>
                    {slots.map((log, i) => (
                        <PerformanceDailyBar key={i} status={log.status} title={log.date} />
                    ))}
                </PerformanceDailyBars>
            )
        }

        return (
            <PerformanceGrid>
                <PerformanceCard>
                    <PerformanceCardHeader>
                        <IconContainer color='#FF99001A'>
                            <ClockIcon />
                        </IconContainer>
                        <div className='header-text'>
                            <span className='title'>Attendance &amp; Presence</span>
                            <span className='source'>Source: HRDL</span>
                        </div>
                    </PerformanceCardHeader>
                    <PerformanceStatsRow>
                        <PerformanceStatCol>
                            <span className='label'>Attendance Rate</span>
                            <div className='value-row'>
                                <span className='value'>{att?.attendanceRate ?? 0}%</span>
                                {!!att?.attendanceRateChange && (
                                    <span className={att.attendanceRateChange >= 0 ? 'change-positive' : 'change-negative'}>
                                        {att.attendanceRateChange > 0 ? '↗' : '↘'} {Math.abs(att.attendanceRateChange)}%
                                    </span>
                                )}
                            </div>
                            <span className='sub-label'>Dept avg {att?.departmentAverageAttendanceRate ?? 0}%</span>
                        </PerformanceStatCol>
                        <PerformanceStatCol>
                            <span className='label'>Shifts Worked</span>
                            <div className='value-row'>
                                <span className='value'>{att?.shiftsWorked ?? 0}/{att?.shiftsScheduled ?? 0}</span>
                            </div>
                            <span className='sub-label'>Scheduled in window</span>
                        </PerformanceStatCol>
                    </PerformanceStatsRow>
                    <PerformanceFootnote>
                        <span className='item no-show'><span className='num'>{att?.noShows ?? 0}</span> no-show</span>
                        <span className='item late'><span className='num'>{att?.lateArrivals ?? 0}</span> late arrivals</span>
                        <span className='item early'><span className='num'>{att?.earlyDepartures ?? 0}</span> early departures</span>
                    </PerformanceFootnote>
                    {renderDailyBars()}
                </PerformanceCard>

                <PerformanceCard>
                    <PerformanceCardHeader>
                        <IconContainer color='#24D07B1A'>
                            <CheckIcon />
                        </IconContainer>
                        <div className='header-text'>
                            <span className='title'>Work Output</span>
                            <span className='source'>Source: Operations</span>
                        </div>
                    </PerformanceCardHeader>
                    <PerformanceDonutContent>
                        <PerformanceStatCol>
                            <span className='label'>Tasks Completed</span>
                            <div className='value-row'>
                                <span className='value'>{tasks?.tasksCompleted ?? 0}</span>
                                {!!tasks?.tasksCompletedRateChange && (
                                    <span className={tasks.tasksCompletedRateChange >= 0 ? 'change-positive' : 'change-negative'}>
                                        {tasks.tasksCompletedRateChange > 0 ? '↗' : '↘'} {Math.abs(tasks.tasksCompletedRateChange)}%
                                    </span>
                                )}
                            </div>
                            <span className='sub-label'>Dept avg{tasks?.departmentAverageTasksCompleted ?? 0}%</span>
                        </PerformanceStatCol>
                        <DonutChart segments={(tasks?.tasksRates || []).map((t, i) => ({
                            name: t.taskName,
                            value: t.taskRate,
                            color: DONUT_COLORS[i % DONUT_COLORS.length],
                        }))} total={totalTasksRate} />
                        <PerformanceLegendList>
                            {(tasks?.tasksRates || []).map((t, i) => (
                                <PerformanceLegendRow key={t.taskName + i}>
                                    <div className='left'>
                                        <span className='dot' style={{ background: DONUT_COLORS[i % DONUT_COLORS.length] }} />
                                        <span className='name'>{t.taskName}</span>
                                    </div>
                                    <span className='pct'>{t.taskRate}%</span>
                                </PerformanceLegendRow>
                            ))}
                        </PerformanceLegendList>
                    </PerformanceDonutContent>
                    <BodyText grey style={{ margin: 0, fontSize: 12 }}>
                        Workflows participated in: {tasks?.workflowsParticipated ?? 0}
                    </BodyText>
                </PerformanceCard>

                <PerformanceCard>
                    <PerformanceCardHeader>
                        <IconContainer color='#FF99001A'>
                            <WrenchIcon />
                        </IconContainer>
                        <div className='header-text'>
                            <span className='title'>Asset Interaction</span>
                            <span className='source'>Source: Machinery + Fuel</span>
                        </div>
                    </PerformanceCardHeader>
                    {(performance?.machines || []).length ? (
                        performance.machines.map((m, i) => (
                            <PerformanceMachineRow key={m + i}>
                                <IconContainer color='#00A6FF1A'>
                                    <TruckIcon />
                                </IconContainer>
                                <div className='text'>
                                    <span className='name'>{m}</span>
                                    <span className='role'>{i === 0 ? 'Primary Assignment' : 'Secondary'}</span>
                                </div>
                                <span className='hours'>-</span>
                            </PerformanceMachineRow>
                        ))
                    ) : (
                        <OtherText grey>No machine usage</OtherText>
                    )}
                </PerformanceCard>

                <PerformanceCard>
                    <PerformanceCardHeader>
                        <IconContainer color='#FF99001A'>
                            <ClockIcon />
                        </IconContainer>
                        <div className='header-text'>
                            <span className='title'>Compliance &amp; Certifications</span>
                            <span className='source'>Source: HR Core</span>
                        </div>
                    </PerformanceCardHeader>
                    <PerformanceStatsRow>
                        <PerformanceStatCol>
                            <span className='label'>Active Certs</span>
                            <div className='value-row'>
                                <span className='value'>{certs?.activeCertificateCount ?? 0}</span>
                            </div>
                            <span className='sub-label'>{certs?.allRequiredCertificatesHeld ? 'All required held' : 'Some missing'}</span>
                        </PerformanceStatCol>
                        <PerformanceStatCol>
                            <span className='label'>Expiring Soon</span>
                            <div className='value-row'>
                                <span className='value'>{certs?.expiringSoonCount ?? 0}</span>
                            </div>
                            <span className='sub-label'>Within 30 days</span>
                        </PerformanceStatCol>
                    </PerformanceStatsRow>
                    {(certs?.expiringSoonCertificates || []).map((c, i) => (
                        <PerformanceCertificateAlert key={c.certificateName + i}>
                            <div className='left'>
                                <PerfAlertIcon />
                                <span className='name'>{c.certificateName}</span>
                                <span className='expires'>expires in {c.expiresInDays} days</span>
                            </div>
                            <button className='action' type='button'>Schedule renewal</button>
                        </PerformanceCertificateAlert>
                    ))}
                </PerformanceCard>
            </PerformanceGrid>
        )
    }

    const renderBasicInfo = () => (
        <Box>
            <EmployeeInfoGrid columns={3}>
                <Field label='First Name' value={basicInfo?.firstName} />
                <Field label='Middle and Last Name' value={basicInfo?.lastName} />
                <Field label='Access Type' value={basicInfo?.accessGroup} />

                <Field label='Username' value={basicInfo?.username} />
                <Field label='Nationality' value={basicInfo?.nationality} />
                <Field label='Educational Level' value={education} />

                <Field label='Phone Number' value={basicInfo?.phoneNumber} />
                <Field label='Sec. Phone Number' value={basicInfo?.secondaryPhoneNumber} />
                <Field label='Email' value={basicInfo?.email} />

                <Field label='National ID' value={basicInfo?.nationalId} />
                <Field label='Marital Status' value={basicInfo?.maritalStatus} />
                <Field label='Department' value={basicInfo?.department} />

                <Field label='Dietary Preference' value={dietary} />
                <Field label='Date of Birth' value={basicInfo?.dateOfBirth ? formatDateForDisplay(basicInfo.dateOfBirth) : '-'} />
                <Field label='Gender' value={genderLabel} />

                <EmployeeSectionHeading>Emergency Contact</EmployeeSectionHeading>

                <Field label='Relative' value={relationLabel} />
                <Field label='Phone Number' value={basicInfo?.emergencyContact?.phoneNumber} />
                <Field label='First Name' value={basicInfo?.emergencyContact?.firstName} />

                <Field label='Middle and Last Name' value={basicInfo?.emergencyContact?.lastName} />
            </EmployeeInfoGrid>
        </Box>
    )

    const renderWelfare = () => (
        <Box>
            <EmployeeInfoGrid columns={3}>
                <Field label='Food' value={dietary} />
                <Field label='Food Allowance External' value={formatNumbersForDisplay(welfare?.foodAllowanceOutCompanyAmount)} />
                <Field label='Transport Allowance Internal' value={formatNumbersForDisplay(welfare?.transportationAllowanceInCompanyAmount)} />

                <Field label='Food Allowance Internal' value={formatNumbersForDisplay(welfare?.foodAllowanceInCompanyAmount)} />
                <Field label='Transport Allowance External' value={formatNumbersForDisplay(welfare?.transportationAllowanceOutCompanyAmount)} />
                <Field label='Transport Type' value={transportType} />

                <Field label='Accommodation Allowance Internal' value={formatNumbersForDisplay(welfare?.accommodationAllowanceInCompanyAmount)} />
                <Field label='Accommodation Allowance External' value={formatNumbersForDisplay(welfare?.accommodationAllowanceOutCompanyAmount)} />
                <Field label='Accommodation Tier' value={accommodationTier} />

                <Field label='Responsibility Allowance Internal' value={formatNumbersForDisplay(welfare?.responsibilityAllowanceInCompanyAmount)} />
                <Field label='Responsibility Allowance External' value={formatNumbersForDisplay(welfare?.responsibilityAllowanceOutCompanyAmount)} />
                <Field label='Extra Duty Allowance Internal' value={formatNumbersForDisplay(welfare?.extraDutyAllowanceInCompanyAmount)} />

                <Field label='Extra Duty Allowance External' value={formatNumbersForDisplay(welfare?.extraDutyAllowanceOutCompanyAmount)} />
            </EmployeeInfoGrid>
        </Box>
    )

    const renderContract = () => {
        const contractList = contracts.data || []
        const selectedFromList = contractId
            ? contractList.find((c) => c.document?.contractId === contractId)
            : null
        const displayedListItem = selectedFromList || contractList[0] || null

        const startDate = contractData?.startDate || displayedListItem?.startDate
        const endDate = contractData?.endDate || displayedListItem?.endDate
        const contractTypeLabel =
            contractTypes.find((t) => t.value == contractData?.type)?.label ||
            contractTypes.find((t) => t.value == displayedListItem?.contractType)?.label ||
            '-'
        const salary = contractData?.value ?? displayedListItem?.salary
        const overtime =
            contractData?.shouldPayOvertime ?? displayedListItem?.overtime ?? false
        const roleName = contractData?.role?.name || displayedListItem?.roleName || header?.roleName
        const workScheduleModelName =
            contractData?.workScheduleModel?.name || displayedListItem?.workScheduleModelName
        const shiftNames =
            (contractData?.shifts || [])
                .map((s) => s?.shift?.name)
                .filter(Boolean)
                .join(', ') || (displayedListItem?.shifts || []).filter(Boolean).join(', ')

        return (
            <Box>
                <EmployeeInfoGrid columns={4}>
                    <Field
                        label='Start Date'
                        value={startDate ? formatDateForDisplay(startDate) : '-'}
                    />
                    <Field
                        label='End Date'
                        value={endDate ? formatDateForDisplay(endDate) : '-'}
                    />
                    <Field label='Contract Type' value={contractTypeLabel} />
                    <EmployeeInfoField>
                        <span className='field-label'>Contracts</span>
                        <EmployeeContractsColumn>
                            {contractList.length ? (
                                contractList.map((c) => (
                                    <EmployeeContractDownloadRow
                                        key={c.document?.contractId}
                                        onClick={() => {
                                            if (c.document?.contractId) {
                                                setContractId(c.document.contractId)
                                            }
                                            setContractModalOpen(true)
                                        }}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <span className='download-name'>
                                            {c.document?.date ? formatDateForDisplay(c.document.date) : '-'}
                                        </span>
                                    </EmployeeContractDownloadRow>
                                ))
                            ) : (
                                <EmployeeContractDownloadRow>
                                    <span className='download-name'>-</span>
                                </EmployeeContractDownloadRow>
                            )}
                        </EmployeeContractsColumn>
                    </EmployeeInfoField>

                    <Field label='Salary' value={salary ? formatNumbersForDisplay(salary) : '-'} />
                    <Field label='Overtime' value={overtime ? 'Yes' : 'No'} />
                    <Field label='Role' value={roleName} />
                    <div />

                    <Field label='Work Schedule Model' value={workScheduleModelName} />
                    <Field label='Shifts' value={shiftNames} />
                </EmployeeInfoGrid>
                {contracts.totalPages > 1 && (
                    <Flex gap='0.5rem' style={{ marginTop: '0.75rem' }}>
                        <Button
                            type='button'
                            disabled={contractsPage <= 1}
                            onClick={() => setContractsPage((p) => Math.max(1, p - 1))}
                        >
                            Previous
                        </Button>
                        <Button
                            type='button'
                            disabled={contractsPage >= contracts.totalPages}
                            onClick={() => setContractsPage((p) => p + 1)}
                        >
                            Next
                        </Button>
                    </Flex>
                )}
            </Box>
        )
    }

    const renderBankInfo = () => (
        <Box>
            <EmployeeInfoGrid columns={3}>
                <Field label='SSNIT' value={bankInfo?.ssnit} />
                <Field label='Account Number' value={bankInfo?.accountNumber} />
                <Field label='Branch Code' value={bankInfo?.branchCode} />

                <Field label='Bank Name' value={bankInfo?.bankName} />
            </EmployeeInfoGrid>
        </Box>
    )

    const renderSkillWallet = () => (
        <Box>
            <OtherText grey>Skill Wallet content coming soon</OtherText>
        </Box>
    )

    const renderDocuments = () => (
        <ListSidebarGrid>
            <ListTable>
                <ListTableHeader>
                    <span>FILE NAME</span>
                    <span>CATEGORY</span>
                    <span>EFFECTIVE DATE</span>
                    <span>EXPIRATION DATE</span>
                </ListTableHeader>
                {documents.data.length ? (
                    documents.data.map((doc) => (
                        <ListTableRow key={doc.id} onClick={() => setPreviewDoc(doc)}>
                            <span className='ellipsis'>{doc.currentUserDocumentFile?.fileName || '-'}</span>
                            <span>{documentCategoryOptions.find((c) => c.value == doc.category)?.label || '-'}</span>
                            <span>{doc.effectiveDate ? formatDateForDisplay(doc.effectiveDate) : '-'}</span>
                            <span>{doc.expiryDate ? formatDateForDisplay(doc.expiryDate) : '-'}</span>
                        </ListTableRow>
                    ))
                ) : (
                    <OtherText grey>No documents</OtherText>
                )}
                {documents.totalPages > 1 && (
                    <Flex gap='0.5rem' style={{ marginTop: '0.5rem' }}>
                        <Button
                            type='button'
                            disabled={docPage <= 1}
                            onClick={() => setDocPage((p) => Math.max(1, p - 1))}
                        >
                            Previous
                        </Button>
                        <Button
                            type='button'
                            disabled={docPage >= documents.totalPages}
                            onClick={() => setDocPage((p) => p + 1)}
                        >
                            Next
                        </Button>
                    </Flex>
                )}
            </ListTable>
            <SidePanelCard>
                <span className='panel-title'>Documents</span>
                <BodyText grey style={{ margin: 0, fontSize: 12 }}>Add file</BodyText>
                <DropZone
                    acceptAll
                    file={docDraft.documentFile}
                    setFile={(file: File) => setDocDraft((p) => ({ ...p, documentFile: file }))}
                />
                <ItemsGrid width='100%' columns={2}>
                    <HonestSelectAsyncInput
                        link='capability'
                        placeholder='Search capability'
                        name='capability'
                        labelText='Capability'
                        onChange={(v) => setDocDraft((p) => ({ ...p, capability: v }))}
                        parserFunction={(data: any) =>
                            data.data.map((item: any) => ({
                                requiresExpiry: item.requiresExpiry,
                                label: item.name,
                                value: item.id,
                            }))
                        }
                        defaultValue={docDraft.capability?.label || ''}
                        nomarg
                        required
                    />
                    <HonestSelectInput
                        nomarg
                        noX
                        name='category'
                        options={documentCategoryOptions}
                        labelText='Category'
                        placeholder='Select...'
                        onChange={(v: any) => setDocDraft((p) => ({ ...p, category: v.value }))}
                        defaultValue={docDraft.category ?? undefined}
                    />
                </ItemsGrid>
                <ItemsGrid width='100%' columns={2}>
                    <HonestSelectInput
                        nomarg
                        noX
                        name='accessRestrictions'
                        options={documentAccessRestrictionsOptions}
                        labelText='Access Restrictions'
                        placeholder='Select...'
                        onChange={(v: any) => setDocDraft((p) => ({ ...p, accessRestrictions: v.value }))}
                        defaultValue={docDraft.accessRestrictions ?? undefined}
                    />
                    <CalendarInput
                        nomarg
                        width='100%'
                        name='effectiveDate'
                        labelText='Effective Date'
                        onChange={(date: Date | null) => setDocDraft((p) => ({ ...p, effectiveDate: date }))}
                        defaultValue={docDraft.effectiveDate}
                    />
                </ItemsGrid>
                <ItemsGrid width='100%' columns={2}>
                    <CalendarInput
                        nomarg
                        width='100%'
                        name='expiryDate'
                        labelText='Expiration Date'
                        minDate={docDraft.effectiveDate}
                        onChange={(date: Date | null) => setDocDraft((p) => ({ ...p, expiryDate: date }))}
                        defaultValue={docDraft.expiryDate}
                    />
                </ItemsGrid>
                <Flex>
                    <Button orange type='button' onClick={handleAddDocument}>
                        Add Document
                    </Button>
                </Flex>
            </SidePanelCard>
        </ListSidebarGrid>
    )

    const renderNotes = () => (
        <ListSidebarGrid>
            <ListTable>
                <ListTableHeader>
                    <span>FILE NAME</span>
                    <span>CATEGORY</span>
                    <span>EFFECTIVE DATE</span>
                    <span>EXPIRATION DATE</span>
                </ListTableHeader>
                {notes.data.length ? (
                    notes.data.map((n) => (
                        <ListTableRow key={n.id} onClick={() => setPreviewNote(n)}>
                            <span className='ellipsis'>{n.content || '-'}</span>
                            <span>{noteCategoryOptions.find((c) => c.value == n.category)?.label || '-'}</span>
                            <span>-</span>
                            <span>-</span>
                        </ListTableRow>
                    ))
                ) : (
                    <OtherText grey>No notes</OtherText>
                )}
                {notes.totalPages > 1 && (
                    <Flex gap='0.5rem' style={{ marginTop: '0.5rem' }}>
                        <Button
                            type='button'
                            disabled={notePage <= 1}
                            onClick={() => setNotePage((p) => Math.max(1, p - 1))}
                        >
                            Previous
                        </Button>
                        <Button
                            type='button'
                            disabled={notePage >= notes.totalPages}
                            onClick={() => setNotePage((p) => p + 1)}
                        >
                            Next
                        </Button>
                    </Flex>
                )}
            </ListTable>
            <SidePanelCard>
                <span className='panel-title'>Notes</span>
                <Input
                    type='textarea'
                    placeholder='Notes'
                    labelText='Notes'
                    nomarg
                    noinvalidtext
                    value={noteDraft.content}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                        setNoteDraft((p) => ({ ...p, content: e.target.value }))
                    }
                    height='100px'
                />
                <ItemsGrid width='100%' columns={2}>
                    <HonestSelectInput
                        nomarg
                        noX
                        name='visibilityScope'
                        options={noteVisibilityScopeOptions}
                        labelText='Visibility Scope'
                        placeholder='Select'
                        onChange={(v: any) => setNoteDraft((p) => ({ ...p, visibilityScope: v.value }))}
                        defaultValue={noteDraft.visibilityScope ?? undefined}
                    />
                    <HonestSelectInput
                        nomarg
                        noX
                        name='category'
                        options={noteCategoryOptions}
                        labelText='Category'
                        placeholder='Select'
                        onChange={(v: any) => setNoteDraft((p) => ({ ...p, category: v.value }))}
                        defaultValue={noteDraft.category ?? undefined}
                    />
                </ItemsGrid>
                <ThinSeparator />
                <Flex>
                    <Button orange type='button' onClick={handleAddNote}>
                        Add Note
                    </Button>
                </Flex>
            </SidePanelCard>
        </ListSidebarGrid>
    )

    const renderTab = () => {
        switch (active) {
            case 'Overview':
                return renderOverview()
            case 'Performance':
                return renderPerformance()
            case 'Basic Info':
                return renderBasicInfo()
            case 'Welfare':
                return renderWelfare()
            case 'Contract':
                return renderContract()
            case 'Bank Info':
                return renderBankInfo()
            case 'Skill Wallet':
                return renderSkillWallet()
            case 'Documents':
                return renderDocuments()
            case 'Notes':
                return renderNotes()
            default:
                return null
        }
    }

    return (
        <>
            <NavBar />
            <Animation>
                <PageWrapper noFooter>
                    <Flex between center>
                        <H1WithBack title='Employee Details' arrow />
                    </Flex>

                    <EmployeeHeaderCard>
                        <EmployeeHeaderTopRow>
                            <EmployeeHeaderIdentity>
                                <EmployeeHeaderAvatar>
                                    <img src={header?.profileImageUrl || Avatar} alt='' />
                                </EmployeeHeaderAvatar>
                                <EmployeeHeaderMeta>
                                    <span className='name'>{header?.fullName}</span>
                                    <div className='role-row'>
                                        <span className='role'>{header?.roleName?.toUpperCase()}</span>
                                        <span className='status'>
                                            <span className='dot' />
                                            {header?.currentAttendance || 'Present'}
                                        </span>
                                    </div>
                                </EmployeeHeaderMeta>
                            </EmployeeHeaderIdentity>

                            <EmployeeHeaderRight>
                                <EmployeePointsPill>
                                    <TrophyIcon />
                                    <span className='value'>{formatNumbersForDisplay(header?.totalRewardPoints ?? 0)}</span>
                                    <span className='label'>POINTS</span>
                                </EmployeePointsPill>
                                <EmployeeRatingInline>
                                    <BigStar />
                                    <span className='value'>{header?.rating ?? '-'}</span>
                                </EmployeeRatingInline>
                            </EmployeeHeaderRight>
                        </EmployeeHeaderTopRow>

                        <EmployeeSchedulecContainer>
                            {renderSchedule().map((entry, i) => (
                                <Schedule entry={entry} key={entry.date || i} />
                            ))}
                        </EmployeeSchedulecContainer>
                    </EmployeeHeaderCard>

                    <TabsContainer style={{ marginTop: '1rem' }}>
                        {TABS.map((tab) => (
                            <Tabs key={tab} active={active === tab} onClick={() => setActive(tab)}>
                                {tab}
                            </Tabs>
                        ))}
                    </TabsContainer>

                    <EmployeeTabPanel>{renderTab()}</EmployeeTabPanel>
                </PageWrapper>
            </Animation>

            {previewDoc && (
                <Modal black>
                    <Flex column gap='1rem' width='100%'>
                        <Flex between width='100%' center style={{ marginBottom: '1rem' }}>
                            <TitleText weight='bold'>Document Preview</TitleText>
                            <CursorPointer onClick={() => setPreviewDoc(null)}>
                                <CloseIcon />
                            </CursorPointer>
                        </Flex>
                        <PreviewModalField>
                            <span className='label'>File</span>
                            {previewDoc.currentUserDocumentFile?.fileUrl ? (
                                <PreviewModalLink
                                    href={previewDoc.currentUserDocumentFile.fileUrl}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                >
                                    Preview PDF
                                </PreviewModalLink>
                            ) : (
                                <span className='value'>-</span>
                            )}
                        </PreviewModalField>
                        <PreviewModalGrid>
                            <PreviewModalField>
                                <span className='label'>Capability</span>
                                <span className='value'>{previewDoc.capability?.code || previewDoc.capability?.name || '-'}</span>
                            </PreviewModalField>
                            <PreviewModalField>
                                <span className='label'>Category</span>
                                <span className='value'>
                                    {documentCategoryOptions.find((c) => c.value == previewDoc.category)?.label || '-'}
                                </span>
                            </PreviewModalField>
                            <PreviewModalField>
                                <span className='label'>Access Restrictions</span>
                                <span className='value'>
                                    {documentAccessRestrictionsOptions.find((r) => r.value == previewDoc.accessRestrictions)?.label || '-'}
                                </span>
                            </PreviewModalField>
                            <PreviewModalField>
                                <span className='label'>Effective Date</span>
                                <span className='value'>{previewDoc.effectiveDate ? formatDateForDisplay(previewDoc.effectiveDate) : '-'}</span>
                            </PreviewModalField>
                            <PreviewModalField>
                                <span className='label'>Expiration Date</span>
                                <span className='value'>{previewDoc.expiryDate ? formatDateForDisplay(previewDoc.expiryDate) : '-'}</span>
                            </PreviewModalField>
                        </PreviewModalGrid>
                    </Flex>
                </Modal>
            )}

            {previewNote && (
                <Modal black>
                    <Flex column gap='1rem' width='100%'>
                        <Flex between width='100%' center style={{ marginBottom: '1rem' }}>
                            <TitleText weight='bold'>Note Preview</TitleText>
                            <CursorPointer onClick={() => setPreviewNote(null)}>
                                <CloseIcon />
                            </CursorPointer>
                        </Flex>
                        <PreviewModalField>
                            <span className='label'>Note</span>
                            <span className='value' style={{ whiteSpace: 'pre-wrap' }}>{previewNote.content || '-'}</span>
                        </PreviewModalField>
                        <PreviewModalGrid>
                            <PreviewModalField>
                                <span className='label'>Visibility Scope</span>
                                <span className='value'>
                                    {previewNote.visibilityScope === 1 ? 'Restricted' : previewNote.visibilityScope === 2 ? 'General' : '-'}
                                </span>
                            </PreviewModalField>
                            <PreviewModalField>
                                <span className='label'>Category</span>
                                <span className='value'>
                                    {previewNote.category === 1 ? 'HR' : previewNote.category === 2 ? 'Ops' : previewNote.category === 3 ? 'Compliance' : previewNote.category === 4 ? 'Welfare' : '-'}
                                </span>
                            </PreviewModalField>
                        </PreviewModalGrid>
                    </Flex>
                </Modal>
            )}

            {contractModalOpen && (
                <ContractViewModal
                    contractData={contractData}
                    closeModal={() => setContractModalOpen(false)}
                    contractTypes={contractTypes}
                    formatDateForDisplay={formatDateForDisplay}
                    formatNumbersForDisplay={formatNumbersForDisplay}
                />
            )}
        </>
    )
}

const ContractViewModal = ({
    contractData,
    closeModal,
    contractTypes,
    formatDateForDisplay,
    formatNumbersForDisplay,
}: {
    contractData: any
    closeModal: () => void
    contractTypes: { value: number; label: string }[]
    formatDateForDisplay: (d: string) => string
    formatNumbersForDisplay: (n: number) => string
}) => {
    useModalClose(closeModal)
    const typeLabel = contractTypes.find((t) => t.value == contractData?.type)?.label || '-'
    const shiftNames = (contractData?.shifts || []).map((s: any) => s?.shift?.name).filter(Boolean)
    const li = contractData?.logisticsInfo
    const transportLabel = logisticsTransportationTypes.find((t) => t.value == li?.transportationType)?.label || '-'
    const tierLabel = tierOptions.find((t) => t.value == li?.accomodationTierPolicy)?.label || '-'

    return (
        <Modal black>
            <Flex column gap='1rem' width='min(100%, 36rem)'>
                <Flex between center width='100%'>
                    <TitleText weight='bold'>
                        CONTRACT {contractData?.startDate ? formatDateForDisplay(contractData.startDate) : '-'}
                    </TitleText>
                    <CursorPointer onClick={closeModal}>
                        <CloseIcon />
                    </CursorPointer>
                </Flex>

                <ContractScrollBody>
                    <Box padding='1rem' width='100%'>
                        <BodyText weight='bold' style={{ marginBottom: '0.75rem' }}>Contract Details</BodyText>
                        <ContractFieldGrid>
                            <ContractField label='Contract Start' value={contractData?.startDate ? formatDateForDisplay(contractData.startDate) : '-'} />
                            <ContractField label='Contract Duration' value={contractData?.endDate ? formatDateForDisplay(contractData.endDate) : '-'} />
                            <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 8 }}>
                                <input type='radio' readOnly checked={false} style={{ accentColor: '#ff9900' }} />
                                <OtherText style={{ margin: 0 }}>Contract indefinite</OtherText>
                            </div>
                            <ContractField label='Contract Type' value={typeLabel} />
                            <ContractField label='Role' value={contractData?.role?.name || '-'} />
                            <ContractField label='Amount' value={contractData?.value ? `$${formatNumbersForDisplay(contractData.value)}` : '-'} />
                            <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 8 }}>
                                <input type='checkbox' readOnly checked={!!contractData?.shouldPayOvertime} style={{ accentColor: '#ff9900' }} />
                                <OtherText style={{ margin: 0 }}>Overtime should be paid</OtherText>
                            </div>
                        </ContractFieldGrid>
                    </Box>

                    <Box padding='1rem' width='100%'>
                        <BodyText weight='bold' style={{ marginBottom: '0.75rem' }}>Work Schedule</BodyText>
                        <ContractFieldGrid>
                            <ContractField label='Vacation Days' value={contractData?.vacationDays ?? '-'} />
                            <ContractField label='Work Schedule Model' value={contractData?.workScheduleModel?.name || '-'} />
                            <ContractField label='Start Date' value={contractData?.workScheduleIntervalStartDate ? formatDateForDisplay(contractData.workScheduleIntervalStartDate) : '-'} />
                        </ContractFieldGrid>
                        {shiftNames.length > 0 && (
                            <>
                                <BodyText weight='bold' style={{ margin: '0.75rem 0' }}>Shifts</BodyText>
                                <ContractFieldGrid>
                                    {shiftNames.map((name: string, i: number) => (
                                        <ContractField key={i} label='Shift' value={name} />
                                    ))}
                                </ContractFieldGrid>
                            </>
                        )}
                    </Box>

                    <Box padding='1rem' width='100%'>
                        <BodyText weight='bold' style={{ marginBottom: '0.75rem' }}>Food</BodyText>
                        <ContractFieldGrid>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <input type='checkbox' readOnly checked={!!li?.hasFoodProvided} style={{ accentColor: '#ff9900' }} />
                                <OtherText style={{ margin: 0 }}>Include Food</OtherText>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <input type='checkbox' readOnly checked={!!li?.hasFoodAllowance} style={{ accentColor: '#ff9900' }} />
                                <OtherText style={{ margin: 0 }}>Food Allowance</OtherText>
                            </div>
                            {li?.hasFoodAllowance && (
                                <>
                                    <ContractField label='Food Allowance (Internal)' value={li?.foodAllowanceInCompanyAmount ?? '-'} />
                                    <ContractField label='Food Allowance (External)' value={li?.foodAllowanceOutCompanyAmount ?? '-'} />
                                </>
                            )}
                        </ContractFieldGrid>
                    </Box>

                    <Box padding='1rem' width='100%'>
                        <BodyText weight='bold' style={{ marginBottom: '0.75rem' }}>Accommodation</BodyText>
                        <ContractFieldGrid>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <input type='checkbox' readOnly checked={!!li?.hasAccommodationProvided} style={{ accentColor: '#ff9900' }} />
                                <OtherText style={{ margin: 0 }}>Include Accommodation</OtherText>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <input type='checkbox' readOnly checked={!!li?.hasAccommodationAllowance} style={{ accentColor: '#ff9900' }} />
                                <OtherText style={{ margin: 0 }}>Accommodation Allowance</OtherText>
                            </div>
                            {li?.hasAccommodationAllowance && (
                                <>
                                    <ContractField label='Accommodation Allowance (Internal)' value={li?.accomodationAllowanceInCompanyAmount ?? '-'} />
                                    <ContractField label='Accommodation Allowance (External)' value={li?.accomodationAllowanceOutCompanyAmount ?? '-'} />
                                </>
                            )}
                            <ContractField label='Accommodation Tier' value={tierLabel} />
                        </ContractFieldGrid>
                    </Box>

                    <Box padding='1rem' width='100%'>
                        <BodyText weight='bold' style={{ marginBottom: '0.75rem' }}>Transportation</BodyText>
                        <ContractFieldGrid>
                            <ContractField label='Transport Type' value={transportLabel} />
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <input type='checkbox' readOnly checked={!!li?.hasTransportationAllowance} style={{ accentColor: '#ff9900' }} />
                                <OtherText style={{ margin: 0 }}>Transport Allowance</OtherText>
                            </div>
                            {li?.hasTransportationAllowance && (
                                <>
                                    <ContractField label='Transport Allowance (Internal)' value={li?.transportationAllowanceInCompanyAmount ?? '-'} />
                                    <ContractField label='Transport Allowance (External)' value={li?.transportationAllowanceOutCompanyAmount ?? '-'} />
                                </>
                            )}
                        </ContractFieldGrid>
                    </Box>

                    <Box padding='1rem' width='100%'>
                        <BodyText weight='bold' style={{ marginBottom: '0.75rem' }}>Responsibility &amp; Extra Duty</BodyText>
                        <ContractFieldGrid>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <input type='checkbox' readOnly checked={!!li?.hasResponsibilityAllowance} style={{ accentColor: '#ff9900' }} />
                                <OtherText style={{ margin: 0 }}>Include Responsibility Allowance</OtherText>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <input type='checkbox' readOnly checked={!!li?.hasExtraDutyAllowance} style={{ accentColor: '#ff9900' }} />
                                <OtherText style={{ margin: 0 }}>Include Extra Duty Allowance</OtherText>
                            </div>
                            {li?.hasResponsibilityAllowance && (
                                <>
                                    <ContractField label='Responsibility Allowance (Internal)' value={li?.responsibilityAllowanceInCompanyAmount ?? '-'} />
                                    <ContractField label='Responsibility Allowance (External)' value={li?.responsibilityAllowanceOutCompanyAmount ?? '-'} />
                                </>
                            )}
                            {li?.hasExtraDutyAllowance && (
                                <>
                                    <ContractField label='Extra Duty Allowance (Internal)' value={li?.extraDutyAllowanceInCompanyAmount ?? '-'} />
                                    <ContractField label='Extra Duty Allowance (External)' value={li?.extraDutyAllowanceOutCompanyAmount ?? '-'} />
                                </>
                            )}
                        </ContractFieldGrid>
                    </Box>
                </ContractScrollBody>
            </Flex>
        </Modal>
    )
}

const ContractScrollBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: 100%;
    max-height: 70vh;
    overflow-y: auto;
    padding-right: 0.25rem;

    ::-webkit-scrollbar {
        width: 4px;
    }
    ::-webkit-scrollbar-track {
        background: transparent;
    }
    ::-webkit-scrollbar-thumb {
        background: ${({ theme }) => theme.colorOrange};
        border-radius: 4px;
    }
`

const ContractFieldGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.75rem;
`

const ContractField = ({ label, value }: { label: string; value: string | number }) => (
    <div>
        <OtherText grey style={{ marginBottom: 4 }}>{label}</OtherText>
        <ContractFieldValue>
            <BodyText style={{ margin: 0 }}>{value}</BodyText>
        </ContractFieldValue>
    </div>
)

const ContractFieldValue = styled.div`
    border: 1px solid ${({ theme }) => theme.colorDividerDefault};
    border-radius: 8px;
    padding: 0.5rem 0.75rem;
    min-height: 2.25rem;
    display: flex;
    align-items: center;
`

const DonutChart = ({
    segments,
    total,
}: {
    segments: { name: string; value: number; color: string }[]
    total: number
}) => {
    const size = 110
    const stroke = 14
    const radius = (size - stroke) / 2
    const circumference = 2 * Math.PI * radius
    const safeTotal = total > 0 ? total : 1
    let offset = 0
    return (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
            <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
                {segments.map((seg, i) => {
                    const length = (seg.value / safeTotal) * circumference
                    const dasharray = `${length} ${circumference - length}`
                    const dashoffset = -offset
                    offset += length
                    return (
                        <circle
                            key={i}
                            cx={size / 2}
                            cy={size / 2}
                            r={radius}
                            fill='none'
                            stroke={seg.color}
                            strokeWidth={stroke}
                            strokeDasharray={dasharray}
                            strokeDashoffset={dashoffset}
                        />
                    )
                })}
            </g>
        </svg>
    )
}
